import { useCallback, useRef, useState } from 'react';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useCreatePatientJoinLink } from '../store/api';
import { copyToClipboard } from '../utils';

interface UsePatientLinkOptions {
  appointmentId: string | undefined;
  encounterId?: string;
}

export interface UsePatientLinkReturn {
  patientUrl: string | null;
  isFetchingLink: boolean;
  handleCopyLink: () => Promise<void>;
  linkCopied: boolean;
}

export function usePatientLink({
  appointmentId,
}: UsePatientLinkOptions): UsePatientLinkReturn {
  const { enqueueSnackbar } = useSnackbar();
  const { telehealth } = useEventTracking();
  const [createPatientLink] = useCreatePatientJoinLink();

  const [patientUrl, setPatientUrl] = useState<string | null>(null);
  const [isFetchingLink, setIsFetchingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Prevent concurrent fetches
  const fetchingRef = useRef(false);

  const fetchPatientUrl = useCallback(async (): Promise<string | null> => {
    if (patientUrl) return patientUrl;
    if (!appointmentId || fetchingRef.current) return null;

    fetchingRef.current = true;
    setIsFetchingLink(true);
    try {
      const result = await createPatientLink({
        joinLinkRequest: { appointment_id: appointmentId },
      }).unwrap();
      setPatientUrl(result.short_url);
      return result.short_url;
    } catch {
      return null;
    } finally {
      setIsFetchingLink(false);
      fetchingRef.current = false;
    }
  }, [appointmentId, patientUrl, createPatientLink]);

  const handleCopyLink = useCallback(async () => {
    if (!appointmentId) return;

    try {
      const url = await fetchPatientUrl();
      if (!url) throw new Error('Failed to generate patient link');

      await copyToClipboard(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      telehealth.linkCopied({ appointment_id: appointmentId });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to copy link';
      enqueueSnackbar(message, { variant: 'error' });
      telehealth.linkCopyFailed({
        appointment_id: appointmentId,
        error_message: message,
      });
    }
  }, [appointmentId, fetchPatientUrl, enqueueSnackbar, telehealth]);

  return {
    patientUrl,
    isFetchingLink,
    handleCopyLink,
    linkCopied,
  };
}
